import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Handshake, ShoppingBag, Megaphone, Users } from 'lucide-react';
import { Lightbulb, Zap, Target } from 'lucide-react';
import { Heart, BookOpen, Gift, Award, Clock } from 'lucide-react';

function MarketingStrategy() {

        const brandROIData = [
        { name: 'Price Premium %', 'Weak Brand': 5, 'Average Brand': 15, 'Strong Brand': 32 },
        { name: 'Customer Retention %', 'Weak Brand': 45, 'Average Brand': 65, 'Strong Brand': 85 },
        { name: 'Market Share Growth %', 'Weak Brand': 2, 'Average Brand': 8, 'Strong Brand': 12 },
    ];
  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Hero Section - Marketing Based on Strategy & Purpose */}
      <section className="relative px-4 sm:px-8 lg:px-24 pt-16 sm:pt-24 pb-12 sm:pb-20">
        {/* Background Image using img tag */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="/Assests/home_page_65.png" 
            alt="Marketing Strategy Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">

          {/* Main Heading */}
          <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight max-w-5xl">
            Marketing Based on Strategy & Purpose
          </h1>

          {/* Subheading */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-4xl mb-12">
            Discover the strategic frameworks that drive exceptional marketing results and align tactical execution with business objectives.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <button className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition">
              Get Started
            </button>
            <button className="px-8 py-3 border border-orange-500 text-orange-500 rounded-md font-medium hover:bg-orange-500 hover:text-white transition">
              Learn More
            </button>
          </div>

        </div>
      </section>

      {/* The Evolution of Strategic Marketing Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20">
        <div className="max-w-7xl mx-auto">
          
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="space-y-8">
              
              {/* Main Heading */}
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
                The Evolution of Strategic Marketing
              </h2>

              {/* First Paragraph */}
              <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
                Modern marketing has evolved far beyond simple product promotion. Today's successful marketing strategies require a sophisticated understanding of diverse approaches, each serving distinct business objectives and audience needs. From building lasting brand equity to driving immediate conversions, the marketing landscape offers a rich toolkit of methodologies.
              </p>

              {/* Second Paragraph */}
              <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
                The most effective marketing organizations don't rely on a single approach. Instead, they orchestrate multiple strategies in harmony, selecting the right methodology for each business challenge. Understanding these core strategic frameworks enables marketing leaders to make informed decisions about resource allocation, campaign design, and long-term brand development.
              </p>

              {/* Third Paragraph */}
              <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
                This guide explores six fundamental marketing strategies that every modern marketing professional should master. Each approach offers unique advantages and addresses specific business challenges, from brand awareness to customer acquisition and retention.
              </p>

            </div>

            {/* Right Side - Image */}
            <div className="relative z-10">
              <div className="relative rounded-3xl overflow-hidden border-1 border-cyan-400 shadow-2xl shadow-cyan-500/50">
                <img 
                  src="/Assests/home_page_81.png" 
                  alt="Marketing Strategy Board" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating glow effects */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
            </div>

          </div>

        </div>
      </section>

      <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
        <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
            Six Strategic Marketing Frameworks
            </h2>

            {/* Six Cards Grid */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* Card 1 - Brand Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Brand Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Building identity and recognition that resonates with target audiences
                </p>
            </div>

            {/* Card 2 - Performance Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Performance Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                ROI-driven campaigns with measurable, trackable outcomes
                </p>
            </div>

            {/* Card 3 - Cause Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Cause Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Aligning with social causes to build purpose-driven connections
                </p>
            </div>

            {/* Card 4 - Experiential Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Experiential Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Creating immersive brand experiences that engage audiences
                </p>
            </div>

            {/* Card 5 - Viral Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Viral Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Designing shareable content that spreads organically online
                </p>
            </div>

            {/* Card 6 - Account-Based Marketing */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-4">Account-Based Marketing</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Hyper-personalized B2B campaigns targeting high-value accounts
                </p>
            </div>
            </div>
        </div>
    </section>

    {/* Section 2: Brand Marketing - Building Lasting Recognition */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
        <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-12">
            {/* Left Side - Content */}
            <div className="space-y-6">
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
                Brand Marketing: Building Lasting Recognition
                </h2>

                <p className="text-gray-300 text-lg leading-relaxed">
                Brand marketing focuses on creating and nurturing the long-term perception of your company in the minds of consumers. Unlike performance marketing's immediate conversion focus, brand marketing plays the long game—building equity, trust, and emotional connections that translate into sustained business value over time.
                </p>

                <p className="text-gray-300 text-lg leading-relaxed">
                This strategic approach encompasses everything from visual identity and messaging to customer experience and brand storytelling. Successful brand marketing creates a distinct personality and position in the market that differentiates you from competitors and commands customer loyalty beyond price considerations.
                </p>
            </div>

            {/* Right Side - Core Components */}
            <div className="space-y-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mt-6 sm:mt-32 mb-6">Core Components</h3>
                <ul className="space-y-4">
                <li className="flex items-start gap-3">
                    <span className="text-orange-500 text-xl">•</span>
                    <span className="text-gray-300 text-lg">Visual identity systems and brand guidelines</span>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-orange-500 text-xl">•</span>
                    <span className="text-gray-300 text-lg">Brand voice and messaging frameworks</span>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-orange-500 text-xl">•</span>
                    <span className="text-gray-300 text-lg">Customer experience design</span>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-orange-500 text-xl">•</span>
                    <span className="text-gray-300 text-lg">Brand storytelling and narrative development</span>
                </li>
                <li className="flex items-start gap-3">
                    <span className="text-orange-500 text-xl">•</span>
                    <span className="text-gray-300 text-lg">Brand positioning and differentiation strategy</span>
                </li>
                </ul>
            </div>
            </div>
        </div>
    </section>
    
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
    <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
        The ROI of Brand Investment
        </h2>

        <p className="text-gray-300 text-lg leading-relaxed mb-12 max-w-5xl">
        While brand marketing's impact may seem intangible compared to direct-response tactics, research consistently demonstrates its measurable business impact. Strong brands command price premiums, enjoy higher customer lifetime value, and weather competitive pressures more effectively.
        </p>

        {/* Chart */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-gray-700">
        <ResponsiveContainer width="100%" height={400}>
            <BarChart data={brandROIData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="name" stroke="#fff" />
            <YAxis stroke="#fff" domain={[0, 90]} />
            <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #f97316' }}
                labelStyle={{ color: '#fff' }}
            />
            <Bar dataKey="Weak Brand" fill="#8B4513" radius={[8, 8, 0, 0]} />
            <Bar dataKey="Average Brand" fill="#CD7F32" radius={[8, 8, 0, 0]} />
            <Bar dataKey="Strong Brand" fill="#FF8C42" radius={[8, 8, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
        </div>

        <p className="text-gray-300 text-lg leading-relaxed mt-8 max-w-5xl">
        Companies with strong brand equity consistently outperform competitors across key metrics. They achieve higher price premiums, retain customers more effectively, and capture greater market share—demonstrating that brand investment delivers tangible financial returns alongside intangible perceptual benefits.
        </p>
    </div>
    </section>

    {/* Section 4: Performance Marketing - Data-Driven Results */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-1 sm:py-2 ">
    <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
        Performance Marketing: Data-Driven Results
        </h2>

        <p className="text-gray-300 text-lg leading-relaxed mb-12 max-w-5xl">
        Performance marketing represents the science-driven counterpart to brand marketing's art. Every dollar invested is tracked, measured, and optimized for maximum return. This approach encompasses paid search, display advertising, affiliate marketing, and social media advertising—all unified by their commitment to measurable outcomes and continuous optimization.
        </p>

        {/* Four Steps Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* Step 1 */}
        <div className="space-y-4">
            <div className="flex items-center gap-4">
            <span className="text-orange-500 text-2xl font-bold">01</span>
            <div className="flex-1 h-px bg-orange-500"></div>
            </div>
            <h3 className="text-2xl font-bold text-white">Campaign Setup</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
            Define clear KPIs, conversion tracking, and attribution models
            </p>
        </div>

        {/* Step 2 */}
        <div className="space-y-4">
            <div className="flex items-center gap-4">
            <span className="text-orange-500 text-2xl font-bold">02</span>
            <div className="flex-1 h-px bg-orange-500"></div>
            </div>
            <h3 className="text-2xl font-bold text-white">Launch & Monitor</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
            Deploy campaigns across channels with real-time performance tracking
            </p>
        </div>

        {/* Step 3 */}
        <div className="space-y-4">
            <div className="flex items-center gap-4">
            <span className="text-orange-500 text-2xl font-bold">03</span>
            <div className="flex-1 h-px bg-orange-500"></div>
            </div>
            <h3 className="text-2xl font-bold text-white">Analyze Data</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
            Identify high-performing segments, creative, and targeting parameters
            </p>
        </div>

        {/* Step 4 */}
        <div className="space-y-4">
            <div className="flex items-center gap-4">
            <span className="text-orange-500 text-2xl font-bold">04</span>
            <div className="flex-1 h-px bg-orange-500"></div>
            </div>
            <h3 className="text-2xl font-bold text-white">Optimize & Scale</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
            Reallocate budget to winning strategies and expand successful campaigns
            </p>
        </div>
        </div>
    </div>
    </section>
    
    {/* Section 5: Performance Marketing Channels */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 bg-orange-500/50">
    <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
        Performance Marketing Channels
        </h2>

        {/* Three Columns Grid */}
        <div className="grid md:grid-cols-3 gap-8">
        {/* Column 1 - Paid Search */}
        <div className="space-y-6">
            <h3 className="text-2xl font-bold text-black">Paid Search (PPC)</h3>
            <p className="text-gray-200 text-base leading-relaxed">
            Search engine advertising that captures high-intent users actively seeking solutions. Google Ads and Bing Ads deliver immediate visibility with precise keyword targeting and bid optimization.
            </p>
            <div className="rounded-2xl overflow-hidden">
            <img 
                src="/Assests/home_page_82.png" 
                alt="Paid Search PPC" 
                className="w-full h-80 object-cover"
            />
            </div>
        </div>

        {/* Column 2 - Paid Social */}
        <div className="space-y-6">
            <h3 className="text-2xl font-bold text-black">Paid Social</h3>
            <p className="text-gray-200 text-base leading-relaxed">
            Platform-native advertising on Facebook, Instagram, LinkedIn, and TikTok. Advanced audience targeting leverages behavioral and demographic data for precise reach.
            </p>
            <div className="rounded-2xl overflow-hidden">
            <img 
                src="/Assests/home_page_83.png" 
                alt="Paid Social" 
                className="w-full h-80 object-cover"
            />
            </div>
        </div>

        {/* Column 3 - Display & Programmatic */}
        <div className="space-y-6">
            <h3 className="text-2xl font-bold text-black">Display & Programmatic</h3>
            <p className="text-gray-200 text-base leading-relaxed">
            Visual banner advertising distributed across publisher networks. Programmatic buying enables automated, data-driven media purchases at scale with real-time optimization.
            </p>
            <div className="rounded-2xl overflow-hidden">
            <img 
                src="/Assests/home_page_84.png" 
                alt="Display & Programmatic" 
                className="w-full h-80 object-cover"
            />
            </div>
        </div>
        </div>
    </div>
    </section>

    {/* Section 6: Key Performance Marketing Metrics */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
    <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
        Key Performance Marketing Metrics
        </h2>

        {/* Four Metrics Grid */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
        {/* Metric 1 */}
        <div className="text-center space-y-4">
            <div className="text-6xl font-bold text-orange-500">3.2x</div>
            <h3 className="text-xl font-bold text-white">Average ROAS</h3>
            <p className="text-gray-400 text-sm">Return on ad spend for optimized performance campaigns</p>
        </div>

        {/* Metric 2 */}
        <div className="text-center space-y-4">
            <div className="text-6xl font-bold text-orange-500">$47</div>
            <h3 className="text-xl font-bold text-white">Median CPA</h3>
            <p className="text-gray-400 text-sm">Cost per acquisition across B2C industries</p>
        </div>

        {/* Metric 3 */}
        <div className="text-center space-y-4">
            <div className="text-6xl font-bold text-orange-500">2.8%</div>
            <h3 className="text-xl font-bold text-white">Conversion Rate</h3>
            <p className="text-gray-400 text-sm">Industry benchmark for paid search campaigns</p>
        </div>

        {/* Metric 4 */}
        <div className="text-center space-y-4">
            <div className="text-6xl font-bold text-orange-500">18%</div>
            <h3 className="text-xl font-bold text-white">CTR Premium</h3>
            <p className="text-gray-400 text-sm">Higher click-through with optimized ad creative</p>
        </div>
        </div>

        <p className="text-gray-300 text-lg leading-relaxed max-w-5xl">
        Success in performance marketing requires relentless focus on these core metrics. Leading organizations establish clear benchmarks, implement sophisticated attribution modeling, and continuously test new approaches to improve efficiency and scale winning strategies.
        </p>
    </div>
    </section>

    {/* Section 7: Cause & CSR Marketing */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
    <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
        Cause & CSR Marketing: Purpose-Driven Branding
        </h2>

        <p className="text-gray-300 text-lg leading-relaxed mb-6 max-w-5xl">
        Cause marketing, also known as Corporate Social Responsibility (CSR) marketing, aligns your brand with social, environmental, or humanitarian causes that resonate with your target audience. This strategic approach goes beyond traditional philanthropy—it integrates purpose into your brand identity and creates authentic connections with increasingly values-driven consumers.
        </p>

        <p className="text-gray-300 text-lg leading-relaxed mb-12 max-w-5xl">
        Modern consumers, particularly Millennials and Gen Z, expect brands to take stands on important issues. Research shows that <span className="text-orange-500 font-semibold">73% of consumers</span> prefer to buy from companies that reflect their personal values. Cause marketing done authentically strengthens brand loyalty, differentiates from competitors, and creates positive social impact while driving business results.
        </p>
    </div>
    </section>

    {/* Section 8: Types of Cause Marketing Initiatives */}

    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 bg-black">
        <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
            Types of Cause Marketing Initiatives
            </h2>

            {/* Four Cards Grid */}
            <div className="grid md:grid-cols-2 gap-5 sm:gap-8">
            {/* Card 1 - Strategic Partnerships */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Handshake className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">Strategic Partnerships</h3>
                <p className="text-gray-300 text-base leading-relaxed">
                    Long-term collaborations with nonprofit organizations that align with brand values. Examples include TOMS' one-for-one giving model or Patagonia's environmental activism partnerships.
                </p>
                </div>
            </div>

            {/* Card 2 - Transaction-Based Giving */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <ShoppingBag className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">Transaction-Based Giving</h3>
                <p className="text-gray-300 text-base leading-relaxed">
                    Donating a portion of sales to charitable causes. This creates a direct connection between consumer purchases and social impact, encouraging sales while supporting worthy causes.
                </p>
                </div>
            </div>

            {/* Card 3 - Advocacy Campaigns */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Megaphone className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">Advocacy Campaigns</h3>
                <p className="text-gray-300 text-base leading-relaxed">
                    Taking public stances on social issues and mobilizing audiences for change. Requires authentic commitment and alignment with brand values to avoid accusations of "cause-washing."
                </p>
                </div>
            </div>

            {/* Card 4 - Employee Engagement Programs */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-orange-500/50 hover:border-orange-500 transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Users className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">Employee Engagement Programs</h3>
                <p className="text-gray-300 text-base leading-relaxed">
                    Mobilizing team members for volunteer work and community service. Builds internal culture while demonstrating genuine organizational commitment to social responsibility.
                </p>
                </div>
            </div>
            </div>
        </div>
    </section>

    {/* Section 9: Best Practices for Cause Marketing */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-9 sm:py-18">
        <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
                Best Practices for Cause Marketing
            </h2>
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            {/* Left Side - Image */}
            <div className="relative">
                <div className="relative rounded-3xl overflow-hidden">
                <img 
                    src="/Assests/home_page_85.png" 
                    alt="Best Practices for Cause Marketing" 
                    className="w-full h-full object-cover"
                />
                </div>
            </div>

            {/* Right Side - Content */}
            <div className="space-y-8">
                {/* Practice 1 */}
                <div className="space-y-3">
                <h3 className="text-2xl font-bold text-white">Authenticity is Non-Negotiable</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                    Consumers can detect inauthentic cause marketing instantly. Choose causes that genuinely align with your brand values and demonstrate long-term commitment beyond one-off campaigns.
                </p>
                </div>

                {/* Practice 2 */}
                <div className="space-y-3">
                <h3 className="text-2xl font-bold text-white">Measure Both Social & Business Impact</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                    Track not only marketing metrics but also the real-world impact of your initiatives. Transparency about outcomes builds credibility and trust with stakeholders.
                </p>
                </div>

                {/* Practice 3 */}
                <div className="space-y-3">
                <h3 className="text-2xl font-bold text-white">Empower Customer Participation</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                    Create mechanisms for customers to join your efforts. User-generated content, matching donations, and volunteer opportunities transform passive consumers into active brand advocates.
                </p>
                </div>
            </div>
            </div>
        </div>
    </section>

    <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
    {/* Animated background particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
        <div
            key={i}
            className="absolute bg-orange-500 rounded-full opacity-20 animate-pulse"
            style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            animationDelay: Math.random() * 3 + 's',
            animationDuration: Math.random() * 3 + 2 + 's'
            }}
        />
        ))}
    </div>

    <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header with gradient text */}
        <div className="text-center mb-20">
        <h2 className="text-2xl sm:text-4xl lg:text-6xl font-extrabold bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent mb-6 leading-tight">
            Experiential Marketing: Creating Memorable Moments
        </h2>
        <p className="text-gray-300 text-xl leading-relaxed max-w-4xl mx-auto">
            Experiential marketing creates immersive, memorable brand interactions that engage consumers on emotional and sensory levels. Moving beyond traditional advertising's one-way communication, experiential campaigns invite participation, create shareable moments, and forge lasting brand connections through direct experience.
        </p>
        </div>

        {/* Main Content Grid - 2x2 */}
        <div className="grid md:grid-cols-2 gap-5 sm:gap-8">
        
        {/* Card 1 - Conceptualize */}
        <div className="group relative bg-orange-500/10 rounded-3xl p-8 border-2 border-orange-500/30 ">
            <div className="relative flex items-start gap-6">
            {/* Large Icon */}
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/50 transition-all duration-500">
                <Lightbulb className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                <h3 className="text-3xl font-bold text-white">Conceptualize</h3>
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                Design experiences that embody brand values and create emotional resonance with your target audience
                </p>
            </div>
            </div>
        </div>

        {/* Card 2 - Activate */}
        <div className="group relative bg-orange-500/10 rounded-3xl p-8 border-2 border-orange-500/30 ">
            <div className="relative flex items-start gap-6">
            {/* Large Icon */}
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/50 transition-all duration-500">
                <Zap className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                <h3 className="text-3xl font-bold text-white">Activate</h3>
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                Execute immersive events that encourage participation and interaction with memorable touchpoints
                </p>
            </div>
            </div>
        </div>

        {/* Card 3 - Amplify */}
        <div className="group relative bg-orange-500/10 rounded-3xl p-8 border-2 border-orange-500/30 "> 
            <div className="relative flex items-start gap-6">
            {/* Large Icon */}
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/50 transition-all duration-500">
                <Megaphone className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                <h3 className="text-3xl font-bold text-white">Amplify</h3>
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                Extend reach through social sharing, PR coverage, and compelling content creation strategies
                </p>
            </div>
            </div>
        </div>

        {/* Card 4 - Measure Impact */}
        <div className="group relative bg-orange-500/10 rounded-3xl p-8 border-2 border-orange-500/30 ">
            <div className="relative flex items-start gap-6">
            {/* Large Icon */}
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/50 transition-all duration-500">
                <Target className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                <h3 className="text-3xl font-bold text-white">Measure Impact</h3>
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                Track engagement, brand lift, and conversion metrics to optimize future experiences and ROI
                </p>
            </div>
            </div>
        </div>

        </div>

    </div>
    </section>

    {/* Section 11: Experiential Marketing Formats */}

    <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-orange-500/60">
    <div className="max-w-7xl mx-auto">
        
        {/* Main Container with dark background */}
        <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-3xl p-5 sm:p-12 border border-gray-700">
        
        {/* Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-orange-500 mb-8 sm:mb-12">
            Experiential Marketing Formats
        </h2>

        {/* Four Columns Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            
            {/* Column 1 - Pop-Up Experiences */}
            <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Pop-Up Experiences</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
                Temporary retail spaces or brand installations that create urgency and exclusivity. Limited-time experiences drive foot traffic and generate buzz in target markets.
            </p>
            </div>

            {/* Column 2 - Event Sponsorships */}
            <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Event Sponsorships</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
                Strategic presence at concerts, festivals, sports events, and conferences. Aligns brand with lifestyle moments and reaches engaged, receptive audiences.
            </p>
            </div>

            {/* Column 3 - Technology-Enabled Experiences */}
            <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Technology-Enabled Experiences</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
                VR, AR, and interactive installations that create wow-moments. Cutting-edge technology amplifies engagement and positions brands as innovative leaders.
            </p>
            </div>

            {/* Column 4 - Sampling & Demonstrations */}
            <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Sampling & Demonstrations</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
                Hands-on product trials in high-traffic locations. Direct experience removes purchase barriers and creates conversion opportunities at point-of-engagement.
            </p>
            </div>

        </div>

        </div>

    </div>
    </section>

    {/* Section 12: Why Experiential Marketing Works */}

    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20">
        <div className="max-w-7xl mx-auto">
            
            {/* Main Container */}
            <div className="">
            
            {/* Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-orange-500 mb-5 sm:mb-6">
                Why Experiential Marketing Works
            </h2>

            {/* Description */}
            <p className="text-gray-300 text-lg leading-relaxed mb-16 max-w-5xl">
                In an increasingly digital world, real-world experiences create powerful differentiation. Experiential marketing leverages psychological principles of active participation and emotional engagement to create lasting brand impressions that passive advertising cannot match.
            </p>

            {/* Four Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12">
                
                {/* Metric 1 - Purchase Intent */}
                <div className="flex flex-col items-center text-center space-y-4">
                {/* Circular Progress */}
                <div className="relative w-40 h-40">
                    <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-gray-700"
                    />
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray="440"
                        strokeDashoffset="66"
                        className="text-orange-500"
                        strokeLinecap="round"
                    />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">85%</span>
                    </div>
                </div>
                
                {/* Label */}
                <h3 className="text-xl font-bold text-white">Purchase Intent</h3>
                <p className="text-gray-400 text-sm">
                    Consumers more likely to buy after brand experience participation
                </p>
                </div>

                {/* Metric 2 - Brand Favorability */}
                <div className="flex flex-col items-center text-center space-y-4">
                {/* Circular Progress */}
                <div className="relative w-40 h-40">
                    <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-gray-700"
                    />
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray="440"
                        strokeDashoffset="114"
                        className="text-orange-500"
                        strokeLinecap="round"
                    />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">74%</span>
                    </div>
                </div>
                
                {/* Label */}
                <h3 className="text-xl font-bold text-white">Brand Favorability</h3>
                <p className="text-gray-400 text-sm">
                    Increase in positive brand perception following experiential engagement
                </p>
                </div>

                {/* Metric 3 - Social Amplification */}
                <div className="flex flex-col items-center text-center space-y-4">
                {/* Circular Progress - Using text instead */}
                <div className="relative w-40 h-40">
                    <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-gray-700"
                    />
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray="440"
                        strokeDashoffset="342"
                        className="text-orange-500"
                        strokeLinecap="round"
                    />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">4.5x</span>
                    </div>
                </div>
                
                {/* Label */}
                <h3 className="text-xl font-bold text-white">Social Amplification</h3>
                <p className="text-gray-400 text-sm">
                    Average social shares generated per event attendee
                </p>
                </div>

                {/* Metric 4 - Brand Recall */}
                <div className="flex flex-col items-center text-center space-y-4">
                {/* Circular Progress */}
                <div className="relative w-40 h-40">
                    <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-gray-700"
                    />
                    <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray="440"
                        strokeDashoffset="40"
                        className="text-orange-500"
                        strokeLinecap="round"
                    />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">91%</span>
                    </div>
                </div>
                
                {/* Label */}
                <h3 className="text-xl font-bold text-white">Brand Recall</h3>
                <p className="text-gray-400 text-sm">
                    Consumers remember brands after experiential activation (vs. 54% for traditional ads)
                </p>
                </div>

            </div>

            </div>

        </div>
    </section>

    {/* Section 13: Viral Marketing - Engineering Shareability */}

    <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-orange-500/60">
    <div className="max-w-7xl mx-auto">
        
        {/* Main Container */}
        <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-3xl p-5 sm:p-12 border border-gray-700">
        
        {/* Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
            Viral Marketing: Engineering Shareability
        </h2>

        {/* First Paragraph */}
        <p className="text-gray-300 text-lg leading-relaxed mb-6">
            Viral marketing creates content designed to spread rapidly through social networks, generating exponential reach through peer-to-peer sharing. While true virality often contains elements of serendipity, successful viral campaigns follow strategic principles that maximize shareability and engagement potential.
        </p>

        {/* Second Paragraph */}
        <p className="text-gray-300 text-lg leading-relaxed">
            The most effective viral marketing doesn't feel like marketing at all. It entertains, surprises, delights, or provides value so compelling that sharing becomes a social currency. Viral content taps into fundamental human emotions—humor, awe, inspiration, or even outrage—that drive people to spread messages within their networks.
        </p>

        </div>

    </div>
    </section>

    {/* Section 14: The Science of Virality */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20">
    {/* Animated background particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
        <div
            key={i}
            className="absolute bg-white rounded-full opacity-20"
            style={{
            width: Math.random() * 3 + 1 + 'px',
            height: Math.random() * 3 + 1 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 5}s linear infinite`,
            animationDelay: `${Math.random() * 5}s`
            }}
        />
        ))}
    </div>

    <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Heading */}
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-16 text-center">
        The Science of Virality
        </h2>

        {/* Six Cards Grid - 2x3 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Card 1 - Emotional Resonance */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Emotional Resonance</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Content that triggers strong emotions (positive or negative) spreads faster. Joy, surprise, and inspiration are particularly potent viral accelerants.
            </p>
            </div>
        </div>

        {/* Card 2 - Narrative Structure */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Narrative Structure</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Compelling stories with clear beginning, middle, and end engage audiences. Human brains are wired for narrative, making story-driven content highly shareable.
            </p>
            </div>
        </div>

        {/* Card 3 - Practical Value */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <Gift className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Practical Value</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Useful information people want to share with friends. Tips, hacks, and insights that help others become social currency for sharers.
            </p>
            </div>
        </div>

        {/* Card 4 - Social Status */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Social Status</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Content that makes sharers look good, knowledgeable, or in-the-know. People share what enhances their social identity and positioning.
            </p>
            </div>
        </div>

        {/* Card 5 - Timeliness & Relevance */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Timeliness & Relevance</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Tapping into current events, trends, and cultural moments. Timely content rides waves of existing attention and conversation.
            </p>
            </div>
        </div>

        {/* Card 6 - Strategic Seeding */}
        <div className="bg-orange-300/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-400 hover:bg-orange-500 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex flex-col items-start space-y-4">
            <div className="w-12 h-12 rounded-lg bg-black/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-gray-800" />
            </div>
            <h3 className="text-2xl font-bold text-black">Strategic Seeding</h3>
            <p className="text-gray-200 text-base leading-relaxed">
                Initial distribution to influencers and engaged communities. The first 24-48 hours determine whether content achieves viral velocity.
            </p>
            </div>
        </div>

        </div>

    </div>

    {/* Add animation keyframes */}
    <style jsx>{`
        @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); }
        25% { transform: translateY(-20px) translateX(10px); }
        50% { transform: translateY(-10px) translateX(-10px); }
        75% { transform: translateY(-30px) translateX(5px); }
        }
    `}</style>
    </section>

    {/* Section 15: Viral Marketing Success Stories */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-6 sm:py-12 bg-orange-500/60">
    <div className="max-w-7xl mx-auto">
        
        {/* Heading */}
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-16">
        Viral Marketing Success Stories
        </h2>

        {/* Two Column Grid */}
        <div className="grid lg:grid-cols-2 gap-12 mb-12">
        
        {/* Story 1 - Dollar Shave Club */}
        <div className="space-y-6">
            <h3 className="text-3xl font-bold text-white">Dollar Shave Club</h3>
            <p className="text-gray-300 text-base leading-relaxed">
            A low-budget video featuring the founder's deadpan humor explaining their subscription model generated 26 million views and disrupted an entire industry. The authentic, irreverent tone resonated with audiences tired of overpriced razor marketing.
            </p>
            
            {/* Image */}
            <div className="rounded-2xl overflow-hidden">
            <img 
                src="/Assests/home_page_86.png" 
                alt="Dollar Shave Club" 
                className="w-full h-full object-cover"
            />
            </div>
        </div>

        {/* Story 2 - ALS Ice Bucket Challenge */}
        <div className="space-y-6">
            <h3 className="text-3xl font-bold text-white">ALS Ice Bucket Challenge</h3>
            <p className="text-gray-300 text-base leading-relaxed">
            User-generated content campaign that raised $115 million for ALS research. Simple participation mechanism, celebrity involvement, and social nomination created unstoppable momentum. Demonstrated how cause-driven viral campaigns can achieve massive scale.
            </p>
            
            {/* Image */}
            <div className="rounded-2xl overflow-hidden">
            <img 
                src="/Assests/home_page_87.png" 
                alt="ALS Ice Bucket Challenge" 
                className="w-full h-full object-cover"
            />
            </div>
        </div>

        </div>

        {/* Key Takeaway Box */}
        <div className="bg-gradient-to-r from-orange-900/50 to-orange-800/50 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-orange-500">
        <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded bg-orange-500/20 flex items-center justify-center mt-1">
            <span className="text-orange-500 text-xl">💡</span>
            </div>
            <div>
            <p className="text-gray-200 text-lg leading-relaxed">
                <span className="font-bold text-white">Key Takeaway:</span> Viral marketing cannot be guaranteed, but strategic application of psychological triggers significantly improves odds of achieving exponential reach and engagement.
            </p>
            </div>
        </div>
        </div>

    </div>
    </section>

    {/* Section 16: Account-Based Marketing - Precision B2B Strategy */}

    <section className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-20 ">
    <div className="max-w-7xl mx-auto">
        
        {/* Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
        Account-Based Marketing: Precision B2B Strategy
        </h2>

        {/* First Paragraph */}
        <p className="text-gray-300 text-lg leading-relaxed mb-6 max-w-5xl">
        Account-Based Marketing (ABM) represents the ultimate in B2B marketing precision. Rather than casting wide nets, ABM treats individual high-value accounts as markets of one, delivering hyper-personalized campaigns tailored to specific organizational needs, decision-makers, and buying processes.
        </p>

        {/* Second Paragraph */}
        <p className="text-gray-300 text-lg leading-relaxed max-w-5xl">
        This strategic approach flips traditional marketing funnels upside down. Instead of generating thousands of leads and filtering for quality, ABM begins with ideal customer identification and invests disproportionate resources in converting specific target accounts. The result: higher win rates, shorter sales cycles, and dramatically improved ROI for enterprise sales organizations.
        </p>

    </div>
    </section>

    {/* Section 17: The ABM Playbook */}
    <section className="relative px-4 sm:px-8 lg:px-24 bg-orange-500/60 py-12">
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-left">
            The ABM Playbook
        </h2>
        <div className="max-w-5xl mx-auto">
            {/* Vertical Timeline */}
            <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-500 via-orange-400 to-orange-500"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
                {/* Step 1 */}
                <div className="relative flex items-start gap-8">
                {/* Number Circle */}
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">1</span>
                </div>

                {/* Content */}
                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Account Selection & Research
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Identify high-value target accounts based on fit, propensity to
                    buy, and strategic value. Conduct deep research into
                    organizational structure, pain points, initiatives, and
                    decision-making processes.
                    </p>
                </div>
                </div>

                {/* Step 2 */}
                <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">2</span>
                </div>

                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Stakeholder Mapping
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Identify all buying committee members and influencers. Map
                    organizational dynamics, individual priorities, and personal
                    preferences to inform personalization strategy.
                    </p>
                </div>
                </div>

                {/* Step 3 */}
                <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">3</span>
                </div>

                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Personalized Content Creation
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Develop account-specific messaging, case studies, and value
                    propositions. Create custom landing pages, presentations, and
                    outreach that speaks directly to identified pain points.
                    </p>
                </div>
                </div>

                {/* Step 4 */}
                <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">4</span>
                </div>

                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Multi-Channel Orchestration
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Execute coordinated touchpoints across email, social, direct
                    mail, advertising, and events. Surround target accounts with
                    consistent, personalized messaging across all channels.
                    </p>
                </div>
                </div>

                {/* Step 5 */}
                <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">5</span>
                </div>

                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Sales Alignment & Execution
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Marketing and sales collaborate on account strategy and execution.
                    Regular coordination ensures seamless handoffs and consistent
                    experience throughout buyer journey.
                    </p>
                </div>
                </div>

                {/* Step 6 */}
                <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-4 border-orange-400/50 flex items-center justify-center z-10">
                    <span className="text-white font-bold text-2xl">6</span>
                </div>

                <div className="flex-1 pt-2">
                    <h3 className="text-2xl font-bold text-white mb-3">
                    Measure & Optimize
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                    Track account engagement, pipeline velocity, and win rates.
                    Continuously refine approach based on what resonates with target
                    accounts and drives progression.
                    </p>
                </div>
                </div>
            </div>
            </div>
        </div>
        </section>

        {/* Section 18: Integrating Strategic Marketing Frameworks */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-4 sm:py-8 ">
        <div className="max-w-7xl mx-auto">
            
            {/* Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-left">
            Integrating Strategic Marketing Frameworks
            </h2>

            <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Content (order-2 on mobile so image shows first) */}
            <div className="space-y-6 order-2 lg:order-1">
                <p className="text-gray-300 text-lg leading-relaxed">
                The most sophisticated marketing organizations don't choose between these strategic frameworks—they integrate them into cohesive, multi-layered programs. Brand marketing builds the foundation of recognition and trust. Performance marketing drives measurable conversions. Cause marketing creates emotional connections and differentiation. Experiential activations bring brands to life. Viral campaigns achieve exponential reach. And ABM delivers enterprise revenue with surgical precision.
                </p>

                <p className="text-gray-300 text-lg leading-relaxed">
                Success requires understanding when to deploy each strategy and how they work together synergistically. Brand equity makes performance campaigns more efficient. Experiential moments fuel viral content. Cause alignment strengthens ABM positioning. The art of strategic marketing lies not in choosing one approach, but in orchestrating multiple methodologies into integrated programs that amplify business impact.
                </p>

                <p className="text-gray-300 text-lg leading-relaxed">
                <span className="font-bold text-white">The path forward:</span> Assess your current marketing mix against these frameworks. Identify gaps and opportunities. Build capabilities in underdeveloped areas. Most importantly, ensure every tactical execution connects to clear strategic objectives that drive measurable business outcomes.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4 pt-6">
                <button className="px-8 py-4 bg-orange-500 text-white rounded-lg font-medium text-lg hover:bg-orange-600 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/50">
                    Start Your Strategy
                </button>
                <button className="px-8 py-4 bg-transparent border-2 border-orange-500 text-orange-500 rounded-lg font-medium text-lg hover:bg-orange-500 hover:text-white transition-all duration-300 hover:scale-105">
                    Download Framework
                </button>
                </div>
            </div>

            {/* Right Side - Image (order-1 on mobile so it shows above text) */}
            <div className="relative order-1 lg:order-2">
                <div className="relative rounded-3xl overflow-hidden border-2 border-orange-500/30 hover:border-orange-500 transition-all duration-300">
                    <img 
                        src="/Assests/home_page_88.png" 
                        alt="Integrating Strategic Marketing Frameworks" 
                        className="w-full h-full object-cover"
                    />
                    </div>
                    
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-orange-500/20 rounded-3xl blur-2xl -z-10"></div>
                </div>
            </div>
        </div>
        </section>

    </div>
  );
}

export default MarketingStrategy;